import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useSMLTheme } from '../../theme/useSMLTheme';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { Panel } from '../../components/primitives/Panel';
import { SMLButton } from '../../components/primitives/SMLButton';
import { isPitchPhase } from './songsBrewCardPhase';
import { songsBrewMessages as messages } from './songsBrewMessages';
import { useSongsBrewHomeCard } from './useSongsBrewHomeCard';
import { SongsBrewMark } from './SongsBrewMark';
import { SongsBrewCardText } from './SongsBrewCardText';
import { SongsBrewIssueList } from './SongsBrewIssueList';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole card; each part uses its own slice of it */
import styles from './SongsBrewHomeCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The SongsBrew newsletter card on the artist Home, after the AI assistants card (design:
 * songsbrew-promotion a1-a10). It renders nothing until `newsletter.getState` answered, so it shows
 * no skeleton. Subscribe sends `source: 'music24-home'`; the close button calls `dismissCard`, and
 * the card does not come back on any platform.
 */
const SongsBrewHomeCardComponent: React.FC = () => {
  const intl = useIntl();
  const titleId = useId();
  const email = useUserTokenDecoded()?.email ?? null;
  const { phase, sendTrackUrl, subscribe, dismiss } = useSongsBrewHomeCard();
  const { theme } = useSMLTheme();

  if (phase === null) {
    return null;
  }

  const isPitch = isPitchPhase(phase);
  const isBusy = phase === 'busy';
  const closeLabel = intl.formatMessage(messages.close);
  return (
    <Panel
      as="section"
      className={clsx(styles.card, theme === 'light' && styles.light)}
      aria-labelledby={titleId}
      data-testid="home-songsbrew"
    >
      <SongsBrewMark isDone={phase === 'done'} />
      <SongsBrewCardText phase={phase} email={email} sendTrackUrl={sendTrackUrl} titleId={titleId} />
      {isPitch && <SongsBrewIssueList />}
      {isPitch && (
        <div className={styles.actions}>
          <SMLButton
            variant="primary"
            className={styles.cta}
            onClick={subscribe}
            disabled={isBusy}
            icon={isBusy ? <span className={styles.spinner} aria-hidden="true" /> : undefined}
          >
            {intl.formatMessage(isBusy ? messages.subscribing : messages.subscribe)}
          </SMLButton>
        </div>
      )}
      <button type="button" className={styles.close} onClick={dismiss} title={closeLabel} aria-label={closeLabel}>
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </Panel>
  );
};

export const SongsBrewHomeCard = TypedMemo(SongsBrewHomeCardComponent);
